import { DuplicateIndicator, IssueIndicator } from "@/components/CaseListing/IssueIndicator";
import { ScanListing } from "@/components/CaseListing/ScanListing";
import { canDrop, ROW_HEIGHT } from "@/components/CaseListing/utils";
import { CloseSvg, CollapsedSvg, ExpandedSvg, ViewSvg } from "@/components/Icon/Icon";
import { ScanThumbnail } from "@/components/Thumbnail/ScanThumbnail";
import {
  CaseState,
  deleteCaseUpload,
  moveSelectedScans,
  selectAreUploadsSorted,
  selectCaseUpload,
  selectDraggingOver,
  setCaseName,
  setDraggingOver,
  setExpandCaseUpload,
  setInspecting,
  setIsInspecting
} from "@/store/slices/casesSlice";
import { PH_NO_CAPTURE } from "@/constants/posthog";
import { RootState, store } from "@/store/store";
import { Box, IconButton, TableCell, TableRow, TextField, Theme, Typography, useTheme } from "@mui/material";
import { Dispatch, FC, KeyboardEventHandler, SetStateAction, useEffect, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { useSelector } from "react-redux";

interface Props {
  uploadId: number;
  status?: string;
  setStatus?: Dispatch<SetStateAction<string>>;
}

interface ScanInspectionButtonProps {
  caseId: number;
  visible?: boolean;
}

const ScanInspectionButton: React.FC<ScanInspectionButtonProps> = ({ caseId, visible }: ScanInspectionButtonProps) => {
  const theme = useTheme();
  return (
    <Box>
      <IconButton
        sx={{ visibility: visible ? "visible" : "hidden" }}
        onClick={() => {
          store.dispatch(setInspecting({ caseIndex: caseId, scanId: null }));
          store.dispatch(setIsInspecting({ isInspecting: true }));
        }}
      >
        <ViewSvg color={theme.surface.onVariant} />
      </IconButton>
    </Box>
  );
};

const sortScanIssues = (
  caseData: CaseState,
  a: { scanId: string; originalIndex: number },
  b: { scanId: string; originalIndex: number }
) => {
  const getGroupAndSeverity = (scanId: string) => {
    // Filtered for this scan
    const errors = caseData.errors.filter((issue) => issue.scanId === scanId);
    const warnings = caseData.warnings.filter((issue) => issue.scanId === scanId);
    if (errors.length > 0) {
      return { group: 0, severity: Math.max(...errors.map((issue) => issue.severity)) };
    }
    if (warnings.length > 0) {
      return { group: 1, severity: Math.max(...warnings.map((issue) => issue.severity)) };
    }
    return { group: 2, severity: 0 };
  };

  const aKey = getGroupAndSeverity(a.scanId);
  const bKey = getGroupAndSeverity(b.scanId);

  if (aKey.group !== bKey.group) {
    return aKey.group - bKey.group;
  }
  // Descending by severity
  if (aKey.group < 2) {
    return bKey.severity - aKey.severity;
  }
  // Original ordering
  return a.originalIndex - b.originalIndex;
};

const CaseNamer = ({ setVisible, name, id }: { setVisible: (state: boolean) => void; name: string; id: number }) => {
  const ref = useRef<HTMLInputElement>(null);
  const handleClose = () => {
    setVisible(false);
    const newName = ref.current?.value?.trim() ?? name;
    store.dispatch(setCaseName({ uploadId: id, newName: newName === "" ? name : newName }));
  };
  const handleKeyUp: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "Enter") {
      handleClose();
    }
  };

  const handleFocus = () => {
    setVisible(true);
  };

  return (
    <TextField
      className={PH_NO_CAPTURE}
      sx={{
        "& input": {
          paddingX: "6px"
        },
        ".MuiInputBase-root": {
          fontSize: "inherit"
        },
        backgroundColor: (theme: Theme) => theme.surface.lowest
      }}
      onKeyUp={handleKeyUp}
      onKeyDown={handleFocus}
      onClick={handleFocus}
      defaultValue={name}
      size="small"
      inputRef={ref}
      onBlur={handleClose}
      fullWidth
    ></TextField>
  );
};

export const CaseListing: FC<Props> = ({ uploadId, status, setStatus }) => {
  const theme = useTheme();

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "scan",
    canDrop: canDrop(uploadId),
    collect: (monitor) => ({
      isOver: monitor.isOver() && monitor.canDrop()
    })
  }));

  useEffect(() => {
    store.dispatch(setDraggingOver({ uploadId, isOver: isOver }));
  }, [isOver, uploadId]);

  const isDraggingOver = useSelector((state: RootState) => selectDraggingOver(state) === uploadId);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const nameRef = useRef<HTMLSpanElement>(null);
  const caseRef = useRef<HTMLTableRowElement | null>(null);
  // This prevents accessing null case while being deleted.
  const defaultCase = {
    uploadId: 0,
    name: "",
    expanded: false,
    areThumbnailsAvailable: false,
    isSelected: false
  };

  const isSorted = useSelector((state: RootState) => selectAreUploadsSorted(state));

  const caseData = useSelector((state: RootState) => selectCaseUpload(state, uploadId)) || defaultCase;

  const handleDeleteCase = async () => {
    await store.dispatch(deleteCaseUpload(uploadId));
  };

  const hasScans = caseData.scans.length !== 0;

  useEffect(() => {
    if (nameRef.current != null) {
      const name = nameRef.current;
      const handlerDBLClick = () => {
        setIsRenaming(true);
      };

      name.addEventListener("dblclick", handlerDBLClick);

      return () => {
        name.removeEventListener("dblclick", handlerDBLClick);
      };
    }
  });

  return (
    <>
      <TableRow
        ref={(node) => {
          drop(node);
          caseRef.current = node;
        }}
        onDrop={() => store.dispatch(moveSelectedScans({ uploadId }))}
        onMouseOver={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        sx={{
          position: "relative",
          outline: (theme: Theme) => `2px solid ${theme.outline.main}`,
          height: `${ROW_HEIGHT}px`,
          backgroundColor: (theme: Theme) => (isHovering ? theme.surface.normal : theme.surface.lowest)
        }}
      >
        <TableCell
          sx={{
            // Creates a dashed drop indicator relative to the table row
            "&::before": {
              content: `""`,
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: "99",
              pointerEvents: "none",
              height: `${caseData.expanded ? ROW_HEIGHT * (caseData.scans.length + 1) : ROW_HEIGHT}px`,
              border: (theme: Theme) => (isDraggingOver ? `2px dashed ${theme.palette.primary.main}` : undefined)
            }
          }}
          padding="checkbox"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            {caseData?.scans.length > 0 && (
              <IconButton
                onClick={() =>
                  store.dispatch(
                    setExpandCaseUpload({
                      uploadId: uploadId,
                      expand: !caseData.expanded
                    })
                  )
                }
                size="small"
              >
                {caseData.expanded ? <ExpandedSvg /> : <CollapsedSvg />}
              </IconButton>
            )}
          </Box>
        </TableCell>
        <TableCell colSpan={2} sx={{ maxWidth: "250px", minWidth: "250px" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              gap: "8px",
              alignItems: "center"
            }}
          >
            <ScanThumbnail caseId={uploadId} />
            {isRenaming || isHovering ? (
              <CaseNamer id={caseData.uploadId} name={caseData.name} setVisible={setIsRenaming} />
            ) : (
              <Typography
                ref={nameRef}
                className={PH_NO_CAPTURE}
                sx={{
                  flex: 1,
                  userSelect: "none",
                  cursor: "text",
                  borderRadius: "4px",
                  paddingLeft: "6px",
                  lineHeight: "39px"
                }}
                noWrap
              >
                {caseData.name}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell></TableCell>
        <TableCell align="right">
          <DuplicateIndicator uploadId={uploadId} />
        </TableCell>
        <TableCell align="right">
          <IssueIndicator uploadId={uploadId} />
        </TableCell>
        <TableCell align="right">
          <ScanInspectionButton caseId={uploadId} visible={isHovering && hasScans} />
        </TableCell>
        <TableCell sx={{ width: "24px" }} align="right">
          <IconButton
            onClick={handleDeleteCase}
            size="small"
            sx={{
              visibility: isHovering ? "visible" : "hidden",
              height: "35px"
            }}
          >
            <CloseSvg color={theme.surface.onVariant} />
          </IconButton>
        </TableCell>
        <TableCell sx={{ paddingRight: "8px !important" }}></TableCell>
      </TableRow>
      {/* Scan Items */}
      {caseData.expanded && (
        <>
          {isSorted // NOTE: It may be that the default ordering is always maintained
            ? caseData.scans
                .map((scanId, originalIndex) => ({
                  scanId,
                  originalIndex
                }))
                // Sort using severity
                .sort((a, b) => sortScanIssues(caseData, a, b))
                // Map to ScanListing components
                .map(({ scanId }) => (
                  <ScanListing
                    key={`${scanId}`}
                    uploadId={uploadId}
                    scanId={scanId}
                    status={status}
                    setStatus={setStatus}
                    disabled={caseData.upperScan !== scanId && caseData.lowerScan !== scanId}
                  />
                ))
            : // unsorted ordering
              caseData.scans.map((scanId) => {
                return (
                  <ScanListing
                    key={scanId}
                    uploadId={uploadId}
                    scanId={scanId}
                    status={status}
                    setStatus={setStatus}
                    disabled={caseData.upperScan !== scanId && caseData.lowerScan !== scanId}
                  />
                );
              })}
        </>
      )}
    </>
  );
};
